const PRODUCT_SEARCH = 'hoyry/product-search';
const TRACK_ROOT = '[data-hoyry-track], [data-burst-goal]';

/**
 * GTM/dataLayer: only when statistics cookies are allowed.
 * No Cookiebot → allow. Cookiebot present but no response yet → deny GTM.
 */
function hasAnalyticsConsent() {
	const cookiebot = window.Cookiebot;
	if ( ! cookiebot || typeof cookiebot.consent !== 'object' ) {
		return true;
	}

	// Banner unanswered — do not push to GTM.
	if ( cookiebot.hasResponse !== true ) {
		return false;
	}

	return cookiebot.consent.statistics === true;
}

/**
 * WP hit consent flag: 1 accepted, 0 denied, -1 unknown (no Cookiebot / pending).
 */
function consentValue() {
	const cookiebot = window.Cookiebot;
	if ( ! cookiebot || typeof cookiebot.consent !== 'object' ) {
		return -1;
	}

	// Pending banner answer is unknown; explicit deny (incl. statistics off) is 0.
	if ( cookiebot.hasResponse !== true ) {
		return -1;
	}

	return cookiebot.consent.statistics === true ? 1 : 0;
}


const TRAFFIC_STORAGE_KEY = 'hoyry_analytics_traffic';
const AD_CLICK_PARAMS = [ 'gclid', 'fbclid', 'msclkid', 'ttclid', 'wbraid', 'gbraid' ];
const JOURNEY_MAX_STEPS = 20;
const PATH_MAX_LEN = 200;
const TITLE_MAX_LEN = 80;

/**
 * When requireStatsConsent is on, traffic sessionStorage + attribution payloads
 * need Cookiebot statistics = true (same gate as GTM). No Cookiebot → allow.
 */
function requireStatsConsentEnabled() {
	return Boolean( window.hoyryAnalytics && window.hoyryAnalytics.requireStatsConsent );
}

function canStoreTrafficAttribution() {
	if ( ! requireStatsConsentEnabled() ) {
		return true;
	}
	return hasAnalyticsConsent();
}

function clearTrafficStore() {
	try {
		sessionStorage.removeItem( 'hoyry_analytics_traffic' );
	} catch ( e ) {
		// ignore
	}
	window.__hoyryAnalyticsTraffic = null;
}

function emptyTrafficAttribution() {
	return {
		referrer: 'direct',
		referrer_path: 'direct',
		utm_source: '',
		utm_medium: '',
		utm_campaign: '',
		ads: false,
		journey: [],
	};
}


/**
 * Sanitize UTM-like string: lowercase, safe charset, max 64.
 */
function sanitizeUtm( value ) {
	return String( value || '' )
		.toLowerCase()
		.replace( /[^a-z0-9._+-]/g, '' )
		.slice( 0, 64 );
}

/**
 * Parse a URL-like value; never keep query or hash.
 *
 * @param {string} raw
 * @returns {URL|null}
 */
function parseUrlNoQuery( raw ) {
	const value = String( raw || '' ).trim();
	if ( ! value ) {
		return null;
	}

	try {
		const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test( value ) || value.startsWith( '//' )
			? value
			: `https://${ value }`;
		const url = new URL( withScheme, window.location.href );
		url.search = '';
		url.hash = '';
		return url;
	} catch ( e ) {
		return null;
	}
}

/**
 * On-site pathname only (no query/hash). Collapses slashes and ".." segments.
 */
function sanitizePathname( value ) {
	let path = '';
	const raw = String( value || '' ).trim();
	if ( ! raw ) {
		return '/';
	}

	if ( /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test( raw ) || raw.startsWith( '//' ) || raw.startsWith( '/' ) ) {
		const url = parseUrlNoQuery( raw.startsWith( '/' ) ? `${ window.location.origin }${ raw }` : raw );
		path = url ? url.pathname || '/' : raw.split( '?' )[ 0 ].split( '#' )[ 0 ];
	} else {
		path = raw.split( '?' )[ 0 ].split( '#' )[ 0 ];
	}

	if ( ! path.startsWith( '/' ) ) {
		path = `/${ path }`;
	}

	const parts = [];
	path.split( '/' ).forEach( ( segment ) => {
		if ( ! segment || segment === '.' ) {
			return;
		}
		if ( segment === '..' ) {
			parts.pop();
			return;
		}
		parts.push( segment );
	} );

	const clean = `/${ parts.join( '/' ) }`;
	return ( clean === '//' ? '/' : clean ).slice( 0, PATH_MAX_LEN ) || '/';
}

/**
 * External hostname only, or "direct" for empty / same-site.
 */
function sanitizeReferrerHost( referrerUrl ) {
	const raw = String( referrerUrl || '' ).trim();
	if ( ! raw || raw === 'direct' ) {
		return 'direct';
	}

	const ref = parseUrlNoQuery( raw );
	if ( ! ref ) {
		return 'direct';
	}

	let host = ( ref.hostname || '' ).toLowerCase();
	host = host.replace( /[^a-z0-9.-]/g, '' ).replace( /^\.+|\.+$/g, '' );
	if ( ! host || ! host.includes( '.' ) ) {
		return 'direct';
	}
	const site = ( window.location.hostname || '' ).toLowerCase().replace( /^www\./, '' );
	const norm = host.replace( /^www\./, '' );
	if ( norm === site ) {
		return 'direct';
	}
	return host.slice( 0, 253 );
}

/**
 * External referrer as host + pathname (no query/hash), or "direct".
 */
function sanitizeReferrerPath( referrerUrl ) {
	const raw = String( referrerUrl || '' ).trim();
	if ( ! raw || raw === 'direct' ) {
		return 'direct';
	}

	if ( sanitizeReferrerHost( raw ) === 'direct' ) {
		return 'direct';
	}

	const ref = parseUrlNoQuery( raw );
	if ( ! ref ) {
		return 'direct';
	}

	const host = ( ref.hostname || '' ).toLowerCase().replace( /[^a-z0-9.-]/g, '' ).replace( /^\.+|\.+$/g, '' );
	const path = sanitizePathname( ref.pathname || '/' );
	return `${ host }${ path }`.slice( 0, PATH_MAX_LEN );
}

/**
 * @param {unknown} raw
 * @returns {Array<{ path: string, at: number, title: string }>}
 */
function sanitizeJourney( raw ) {
	if ( ! Array.isArray( raw ) ) {
		return [];
	}

	const out = [];
	raw.forEach( ( step ) => {
		if ( ! step || typeof step !== 'object' ) {
			return;
		}
		const path = sanitizePathname( step.path || step.pathname || '' );
		const at = Number.isFinite( Number( step.at ) ) ? Math.floor( Number( step.at ) ) : 0;
		const title = String( step.title || '' ).replace( /\s+/g, ' ' ).trim().slice( 0, TITLE_MAX_LEN );
		const last = out[ out.length - 1 ];
		if ( last && last.path === path ) {
			return;
		}
		out.push( { path, at, title } );
	} );

	if ( out.length <= JOURNEY_MAX_STEPS ) {
		return out;
	}

	return [ out[ 0 ], ...out.slice( -( JOURNEY_MAX_STEPS - 1 ) ) ];
}

function currentPathStep() {
	return {
		path: sanitizePathname( window.location.pathname || '/' ),
		at: Math.floor( Date.now() / 1000 ),
		title: String( document.title || '' ).replace( /\s+/g, ' ' ).trim().slice( 0, TITLE_MAX_LEN ),
	};
}

function readTrafficStore() {
	try {
		const raw = sessionStorage.getItem( TRAFFIC_STORAGE_KEY );
		if ( raw ) {
			const parsed = JSON.parse( raw );
			if ( parsed && typeof parsed === 'object' ) {
				return parsed;
			}
		}
	} catch ( e ) {
		if ( window.__hoyryAnalyticsTraffic && typeof window.__hoyryAnalyticsTraffic === 'object' ) {
			return window.__hoyryAnalyticsTraffic;
		}
	}

	return null;
}

function writeTrafficStore( attribution ) {
	try {
		sessionStorage.setItem( TRAFFIC_STORAGE_KEY, JSON.stringify( attribution ) );
	} catch ( e ) {
		window.__hoyryAnalyticsTraffic = attribution;
	}
}

/**
 * First page view in the tab wins for landing attribution.
 * Journey is appended on every page (consecutive duplicate pathnames collapsed).
 */
function captureTrafficAttribution() {
	if ( ! canStoreTrafficAttribution() ) {
		return;
	}

	let stored = readTrafficStore();

	if ( ! stored ) {
		const params = new URLSearchParams( window.location.search || '' );
		stored = {
			referrer: sanitizeReferrerHost( document.referrer ),
			referrer_path: sanitizeReferrerPath( document.referrer ),
			utm_source: sanitizeUtm( params.get( 'utm_source' ) ),
			utm_medium: sanitizeUtm( params.get( 'utm_medium' ) ),
			utm_campaign: sanitizeUtm( params.get( 'utm_campaign' ) ),
			ads: AD_CLICK_PARAMS.some( ( name ) => params.has( name ) ),
			journey: [],
		};
	}

	stored.journey = sanitizeJourney( [ ...( stored.journey || [] ), currentPathStep() ] );
	writeTrafficStore( stored );
}

/**
 * @returns {{
 *   referrer: string,
 *   referrer_path: string,
 *   utm_source: string,
 *   utm_medium: string,
 *   utm_campaign: string,
 *   ads: boolean,
 *   journey: Array<{ path: string, at: number, title: string }>
 * }}
 */
function getTrafficAttribution() {
	if ( ! canStoreTrafficAttribution() ) {
		return emptyTrafficAttribution();
	}

	const stored = readTrafficStore();

	if ( ! stored ) {
		return {
			referrer: 'direct',
			referrer_path: 'direct',
			utm_source: '',
			utm_medium: '',
			utm_campaign: '',
			ads: false,
			journey: sanitizeJourney( [ currentPathStep() ] ),
		};
	}

	const host = String( stored.referrer || 'direct' ).toLowerCase();
	const referrer = host === 'direct' ? 'direct' : sanitizeReferrerHost( host );
	const storedPath = String( stored.referrer_path || '' ).trim();

	return {
		referrer,
		referrer_path: storedPath ? sanitizeReferrerPath( storedPath ) : ( referrer === 'direct' ? 'direct' : '' ),
		utm_source: sanitizeUtm( stored.utm_source ),
		utm_medium: sanitizeUtm( stored.utm_medium ),
		utm_campaign: sanitizeUtm( stored.utm_campaign ),
		ads: Boolean( stored.ads ),
		journey: sanitizeJourney( stored.journey ),
	};
}

function attributionPayload() {
	const attr = getTrafficAttribution();
	return {
		referrer: attr.referrer,
		referrer_path: attr.referrer_path,
		utm_source: attr.utm_source,
		utm_medium: attr.utm_medium,
		utm_campaign: attr.utm_campaign,
		ads: attr.ads ? 1 : 0,
		journey: attr.journey,
	};
}

/** Hidden field names for Fluent Forms server-side attribution. */
const FLUENT_ATTR_FIELDS = {
	_hoyry_ref: 'referrer',
	_hoyry_ref_path: 'referrer_path',
	_hoyry_utm_source: 'utm_source',
	_hoyry_utm_medium: 'utm_medium',
	_hoyry_utm_campaign: 'utm_campaign',
	_hoyry_ads: 'ads',
	_hoyry_journey: 'journey',
};

function fluentFieldValue( attr, key ) {
	if ( key === 'ads' ) {
		return attr.ads ? '1' : '0';
	}
	if ( key === 'journey' ) {
		return JSON.stringify( attr.journey || [] );
	}
	return String( attr[ key ] ?? '' );
}

function ensureFluentAttributionFields( form ) {
	if ( ! form || form.nodeName !== 'FORM' ) {
		return;
	}

	const attr = getTrafficAttribution();
	Object.keys( FLUENT_ATTR_FIELDS ).forEach( ( name ) => {
		let input = form.querySelector( `input[name="${ name }"]` );
		if ( ! input ) {
			input = document.createElement( 'input' );
			input.type = 'hidden';
			input.name = name;
			form.appendChild( input );
		}
		input.value = fluentFieldValue( attr, FLUENT_ATTR_FIELDS[ name ] );
	} );
}

function injectFluentAttributionFields() {
	document.querySelectorAll( 'form.frm-fluent-form, form[data-form_id], .fluentform form' ).forEach( ( form ) => {
		ensureFluentAttributionFields( form );
	} );
}

function restConfig() {
	return {
		restUrl: window.hoyryAnalytics?.restUrl || '/wp-json/hoyry-analytics/v1',
		nonce: window.hoyryAnalytics?.nonce || '',
	};
}

/**
 * Fire-and-forget POST that must survive navigation (CTA click → new page).
 * sendBeacon cannot set X-WP-Nonce; the nonce is passed as `_wpnonce` (query).
 * Fallback keeps the same JSON body and header auth via keepalive fetch.
 */
function postJson( path, body ) {
	const { restUrl, nonce } = restConfig();
	const url = `${ restUrl }${ path }`;
	const json = JSON.stringify( body );

	if ( typeof navigator.sendBeacon === 'function' ) {
		const separator = url.includes( '?' ) ? '&' : '?';
		const beaconUrl = `${ url }${ separator }_wpnonce=${ encodeURIComponent( nonce ) }`;
		try {
			const queued = navigator.sendBeacon(
				beaconUrl,
				new Blob( [ json ], { type: 'application/json' } )
			);
			if ( queued ) {
				return;
			}
		} catch ( e ) {
			// Fall through to keepalive fetch.
		}
	}

	return fetch( url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce,
		},
		credentials: 'same-origin',
		keepalive: true,
		body: json,
	} ).catch( () => {} );
}

function pushToDataLayer( payload ) {
	const goalId = payload.goalId || '';

	if ( hasAnalyticsConsent() ) {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push( {
			event: 'hoyry_cta',
			hoyry_label: payload.label || '',
			hoyry_block: payload.block || '',
			hoyry_link_url: payload.linkUrl || '',
			hoyry_track: payload.track || 'cta_click',
			eventCallback: goalId ? () => postGtmAck( goalId ) : undefined,
		} );
	}

	postGoalHit( payload );
}

function postGoalHit( payload ) {
	const goalId = payload.goalId || '';
	if ( ! goalId ) {
		return;
	}

	postJson( '/hit', {
		goal_id: goalId,
		consent: consentValue(),
		...attributionPayload(),
	} );
}

function postGtmAck( goalId ) {
	if ( ! goalId ) {
		return;
	}

	postJson( '/gtm-ack', {
		goal_id: goalId,
	} );
}

function postGtmAckForForm( formId ) {
	if ( ! formId ) {
		return;
	}

	postJson( '/gtm-ack', {
		form_id: String( formId ),
	} );
}

function textOf( element ) {
	return ( element?.textContent || '' ).replace( /\s+/g, ' ' ).trim().slice( 0, 120 );
}

function payloadFromRoot( root, clickable ) {
	const label =
		root.getAttribute( 'data-hoyry-track-label' ) ||
		textOf( clickable ) ||
		textOf( root );

	let linkUrl = '';
	if ( clickable instanceof HTMLAnchorElement && clickable.href ) {
		linkUrl = clickable.href;
	} else if ( clickable?.getAttribute?.( 'href' ) ) {
		linkUrl = clickable.getAttribute( 'href' );
	}

	return {
		track: root.getAttribute( 'data-hoyry-track' ) || 'cta_click',
		label,
		block: root.getAttribute( 'data-hoyry-track-block' ) || '',
		linkUrl,
		goalId: root.getAttribute( 'data-hoyry-goal-id' ) || '',
	};
}

function trackRoot( target ) {
	return target?.closest?.( TRACK_ROOT ) || null;
}

function track( payload ) {
	if ( ! payload || typeof payload !== 'object' ) {
		return;
	}

	pushToDataLayer( {
		track: payload.track || payload.hoyry_track || 'cta_click',
		label: payload.label || payload.hoyry_label || '',
		block: payload.block || payload.hoyry_block || '',
		linkUrl: payload.linkUrl || payload.hoyry_link_url || '',
		goalId: payload.goalId || '',
	} );
}

function isProductSearch( root ) {
	return root.getAttribute( 'data-hoyry-track-block' ) === PRODUCT_SEARCH;
}

function onClick( event ) {
	const root = trackRoot( event.target );
	if ( ! root ) {
		return;
	}

	if ( isProductSearch( root ) ) {
		if ( ! event.target.closest( '.hoyry-product-search-show-all' ) ) {
			return;
		}
		track( payloadFromRoot( root, event.target ) );
		return;
	}

	const clickable = event.target.closest( 'a, button, input[type="submit"], input[type="button"]' );
	track( payloadFromRoot( root, clickable || event.target ) );
}

function onSearchEnter( event ) {
	if ( event.key !== 'Enter' ) {
		return;
	}

	const input = event.target.closest?.( '.hoyry-product-search-input' );
	if ( ! input ) {
		return;
	}

	const root = trackRoot( input );
	if ( ! root || ! isProductSearch( root ) ) {
		return;
	}

	track( payloadFromRoot( root, input ) );
}

function onSubmit( event ) {
	const root = trackRoot( event.target );
	if ( ! root || ! isProductSearch( root ) ) {
		return;
	}

	track( payloadFromRoot( root, event.submitter || event.target ) );
}

/**
 * Fluent CustomEvent detail is { form, config, response }, not { form_id }.
 * Form element has data-form_id (see Fluent FormBuilder).
 */
function formIdFromFluent( detail ) {
	if ( ! detail || typeof detail !== 'object' ) {
		return '';
	}

	const formEl = detail.form;
	const fromDom =
		( formEl && typeof formEl.getAttribute === 'function'
			? formEl.getAttribute( 'data-form_id' )
			: '' ) ||
		formEl?.dataset?.form_id ||
		formEl?.dataset?.formId ||
		'';

	const config = detail.config || {};
	const response = detail.response || {};
	const fromConfig = config.id || config.form_id || config.formId || '';
	const fromResponse =
		response.form_id ||
		response.formId ||
		response.data?.form_id ||
		response.data?.formId ||
		'';

	const raw =
		fromDom ||
		fromConfig ||
		fromResponse ||
		detail.form_id ||
		detail.formId ||
		detail.id ||
		'';

	const id = parseInt( String( raw ), 10 );
	return id > 0 ? String( id ) : '';
}

function postFluentHitAttribution( formId ) {
	if ( ! formId ) {
		return;
	}

	postJson( '/hit-attribution', {
		form_id: String( formId ),
		...attributionPayload(),
	} );
}

function pushFluentFormSubmit( detail ) {
	const formId = formIdFromFluent( detail );
	if ( ! formId ) {
		return;
	}

	if ( hasAnalyticsConsent() ) {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push( {
			event: 'hoyry_form_submit',
			form_id: formId,
			eventCallback: () => postGtmAckForForm( formId ),
		} );
	}

	// Server already counted the hit; send sessionStorage attribution only.
	postFluentHitAttribution( formId );
}

window.hoyry = window.hoyry || {};
window.hoyry.track = track;

function bootTrafficAttribution() {
	if ( canStoreTrafficAttribution() ) {
		captureTrafficAttribution();
		injectFluentAttributionFields();
		return;
	}
	clearTrafficStore();
	injectFluentAttributionFields();
}

bootTrafficAttribution();

window.addEventListener( 'CookiebotOnAccept', bootTrafficAttribution );
window.addEventListener( 'CookiebotOnDecline', () => {
	if ( requireStatsConsentEnabled() ) {
		clearTrafficStore();
		injectFluentAttributionFields();
	}
} );
window.addEventListener( 'CookiebotOnConsentReady', bootTrafficAttribution );

document.addEventListener( 'click', onClick, true );
document.addEventListener( 'keydown', onSearchEnter, true );
document.addEventListener( 'submit', ( event ) => {
	onSubmit( event );
	const form = event.target;
	if ( form && form.nodeName === 'FORM' ) {
		ensureFluentAttributionFields( form );
	}
}, true );

document.addEventListener( 'fluentform_submission_success', ( event ) => {
	pushFluentFormSubmit( event.detail || {} );
} );

document.addEventListener( 'fluentform_init', () => {
	injectFluentAttributionFields();
} );

if ( window.jQuery ) {
	window.jQuery( document.body ).on(
		'fluentform_submission_success',
		( _event, detail ) => {
			pushFluentFormSubmit( detail || {} );
		}
	);
	window.jQuery( document ).on( 'fluentform_init', () => {
		injectFluentAttributionFields();
	} );
}

// Fluent may render forms async; refresh hidden fields when DOM grows.
if ( typeof MutationObserver === 'function' ) {
	let scheduled = null;
	const observer = new MutationObserver( () => {
		if ( scheduled ) {
			return;
		}
		scheduled = setTimeout( () => {
			scheduled = null;
			injectFluentAttributionFields();
		}, 200 );
	} );
	observer.observe( document.documentElement, { childList: true, subtree: true } );
}
